import { useState, useRef, useEffect } from "react";
import wrapperHOC from "@Utils/wrapperHOC";
import Portal from "@Components/Portal";
import lockBody from "@Utils/lockBody";
import unlockBody from "@Utils/unLockBody";
import styles from "../neighbourhoodSection.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

export interface Destination {
  id: string;
  name: string;
  lat?: number;
  lng?: number;
  isCityCentre?: boolean;
}

interface DestinationSelectorProps {
  destinations: Destination[];
  selected: Destination;
  onSelect: (dest: Destination) => void;
  isMobile?: boolean;
}

const DestinationSelector = ({
  destinations,
  selected,
  onSelect,
  isMobile,
}: DestinationSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState<Destination>(selected);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync temp selection when sheet opens
  useEffect(() => {
    if (isOpen) setTempSelected(selected);
  }, [isOpen, selected]);

  // Close dropdown on outside click (desktop only)
  useEffect(() => {
    if (isMobile) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isMobile && isOpen) {
      lockBody();
    }
    return () => {
      unlockBody();
    };
  }, [isMobile, isOpen]);

  if (isMobile) {
    return (
      <>
        <div className={styles.destinationMobileWrapper}>
          <span className={styles.destinationMobileLabel}>Commute to</span>
          <button
            type="button"
            className={styles.destinationMobileSelect}
            onClick={() => setIsOpen(true)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-label="Select destination"
          >
            <span>{selected.name}</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6L8 10L12 6"
                stroke="#374151"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {isOpen && (
          <Portal rootId="forPortal">
            <div
              className={styles.bottomsheetOverlay}
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setIsOpen(false);
              }}
              role="button"
              tabIndex={0}
              aria-label="Close bottomsheet"
            >
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <div
                className={styles.bottomsheetContent}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Duration"
                tabIndex={-1}
              >
                <div className={styles.bottomsheetHeader}>
                  <h3>Duration</h3>
                  <button type="button" onClick={() => setIsOpen(false)} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M15 5L5 15M5 5l10 10"
                        stroke="#374151"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                <div className={styles.bottomsheetList}>
                  {destinations.map((dest) => (
                    <button
                      key={dest.id}
                      type="button"
                      className={styles.bottomsheetItem}
                      onClick={() => setTempSelected(dest)}
                    >
                      <span
                        className={formatClassNames(
                          styles.bottomsheetRadio,
                          tempSelected.id === dest.id && styles.bottomsheetRadioActive,
                        )}
                      />
                      {dest.name}
                    </button>
                  ))}
                </div>

                <div className={styles.bottomsheetFooter}>
                  <button
                    type="button"
                    className={styles.bottomsheetClear}
                    onClick={() => {
                      setTempSelected(destinations[0]);
                    }}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className={styles.bottomsheetApply}
                    onClick={() => {
                      onSelect(tempSelected);
                      setIsOpen(false);
                    }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </Portal>
        )}
      </>
    );
  }

  // Desktop: the same "Commute to" select as mobile, sitting above the card,
  // with an inline dropdown instead of a bottom sheet (P1D-5482 iteration 2).
  return (
    <div className={styles.destinationMobileWrapper} ref={dropdownRef}>
      <span className={styles.destinationMobileLabel}>Commute to</span>
      <button
        type="button"
        className={styles.destinationMobileSelect}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select destination"
      >
        <span>{selected.name}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className={formatClassNames(
            styles.destinationChevron,
            isOpen && styles.destinationChevronOpen,
          )}
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="#374151"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.destinationDropdown} role="listbox">
          {destinations.map((dest) => (
            <button
              key={dest.id}
              type="button"
              role="option"
              aria-selected={selected.id === dest.id}
              className={formatClassNames(
                styles.destinationOption,
                selected.id === dest.id && styles.destinationOptionActive,
              )}
              onClick={() => {
                onSelect(dest);
                setIsOpen(false);
              }}
            >
              {dest.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(DestinationSelector, {
  componentName: "DestinationSelector",
  showForChina: false,
  showForWhiteLable: false,
});
